import React, { useCallback, useMemo, useState } from 'react'
import { Box, Popover, PopoverContent, PopoverTrigger, PseudoBox, useDisclosure } from '@chakra-ui/core'
import { FixedSizeList, ListChildComponentProps } from 'react-window'
import { SearchInput } from 'components/ui/Input/SearchInput'

interface IVirtualMenuProps<T> {
  items: T[]
  renderItem: (item: T, isSelected: boolean) => React.ReactNode
  onSelect: (item: T) => void
  getItemId: (item: T) => string
  selectedIds?: string[]
  trigger: React.ReactElement
  searchable?: boolean
  searchPlaceholder?: string
  filterItem?: (item: T, query: string) => boolean
  estimatedItemHeight?: number
  maxVisibleItems?: number
  menuWidth?: number | string
  placement?: 'bottom' | 'bottom-start' | 'bottom-end' | 'top' | 'top-start' | 'top-end'
  emptyLabel?: string
}

interface IRowData<T> {
  items: T[]
  selectedIdsSet: Set<string>
  getItemId: (item: T) => string
  renderItem: (item: T, isSelected: boolean) => React.ReactNode
  onSelect: (item: T) => void
  itemHeight: number
}

// Module-scope: stable reference so react-window never unmounts rows on parent re-render
function Row<T>({ index, style, data }: ListChildComponentProps) {
  const { items, selectedIdsSet, getItemId, renderItem, onSelect, itemHeight } = data as IRowData<T>
  const item = items[index]
  const id = getItemId(item)
  const isSelected = selectedIdsSet.has(id)

  return (
    <div style={style}>
      <PseudoBox
        as="div"
        mx="4px"
        px="8px"
        h={`${itemHeight - 4}px`}
        display="flex"
        alignItems="center"
        borderRadius="6px"
        bg={isSelected ? '#F1F5F9' : 'transparent'}
        style={{ cursor: 'pointer', transition: 'background 0.1s ease-out' }}
        onClick={() => onSelect(item)}
        _hover={{ bg: isSelected ? '#E8EEF5' : '#F8FAFC' }}
      >
        {renderItem(item, isSelected)}
      </PseudoBox>
    </div>
  )
}

const DEFAULT_ITEM_HEIGHT = 44
const DEFAULT_MAX_VISIBLE = 8

export const VirtualMenu = <T,>({
  items,
  renderItem,
  onSelect,
  getItemId,
  selectedIds = [],
  trigger,
  searchable = false,
  searchPlaceholder = 'Search…',
  filterItem,
  estimatedItemHeight = DEFAULT_ITEM_HEIGHT,
  maxVisibleItems = DEFAULT_MAX_VISIBLE,
  menuWidth = 260,
  placement = 'bottom-start',
  emptyLabel = 'No results',
}: IVirtualMenuProps<T>) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [query, setQuery] = useState('')

  const filteredItems = useMemo(() => {
    if (!searchable || !query.trim() || !filterItem) return items
    return items.filter((item) => filterItem(item, query))
  }, [items, searchable, query, filterItem])

  const listHeight = Math.max(1, Math.min(filteredItems.length, maxVisibleItems)) * estimatedItemHeight

  const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const itemData = useMemo<IRowData<T>>(
    () => ({ items: filteredItems, selectedIdsSet, getItemId, renderItem, onSelect, itemHeight: estimatedItemHeight }),
    [filteredItems, selectedIdsSet, getItemId, renderItem, onSelect, estimatedItemHeight]
  )

  const handleOpen = useCallback(() => {
    setQuery('')
    onOpen()
  }, [onOpen])

  return (
    <Popover isOpen={isOpen} onOpen={handleOpen} onClose={onClose} placement={placement} usePortal closeOnBlur>
      <PopoverTrigger>{trigger}</PopoverTrigger>

      <PopoverContent
        w={menuWidth}
        borderRadius="10px"
        boxShadow="0 4px 24px rgba(15,23,42,0.10), 0 1px 4px rgba(15,23,42,0.06)"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="#E2E8F0"
        overflow="hidden"
        _focus={{ outline: 'none' }}
        zIndex={1400}
      >
        {searchable && (
          <Box px="8px" pt="8px" pb="4px">
            <SearchInput
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClear={() => setQuery('')}
              placeholder={searchPlaceholder}
              elementHeight="36px"
            />
          </Box>
        )}

        <Box py="4px">
          {filteredItems.length === 0 ? (
            <Box px="16px" py="12px" fontSize="13px" color="#94A3B8" textAlign="center">
              {emptyLabel}
            </Box>
          ) : (
            <FixedSizeList
              height={listHeight}
              itemCount={filteredItems.length}
              itemSize={estimatedItemHeight}
              width="100%"
              overscanCount={3}
              itemData={itemData}
            >
              {Row}
            </FixedSizeList>
          )}
        </Box>
      </PopoverContent>
    </Popover>
  )
}
